import React, {
  CSSProperties,
  Suspense,
  useEffect,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import throttle from 'lodash.throttle';
import { decode, encode } from 'html-entities';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { gsap } from 'gsap';
import {
  Data,
  FacetId,
  Filter,
  FilterFacet,
  FilterName,
  HandleGroupSelectAllArgs,
  HandleMassSelectArgs,
  HandleSearchTermArgs,
  HandleSelectedArgs,
  PageData,
  Result,
} from './types';
import rootRender from '@react/utils/root-render';
import Button from '@react/components/button';
import { useIsBreakpoint, useOutsideClick } from '@react/utils/hooks';
import { URL_SEARCH_FILTERS, URL_SEARCH_SEARCH } from '@/js/lib/constants';
import Checkbox from './checkbox';
import FilterMenu from './filter-menu';
import { getDataFromPage } from '@react/utils';
import Search, { InlineSearch } from './search';
import {
  CardResult,
  EventResult,
  PersonResult,
  PopularSearches,
  SearchResult,
  Skeleton,
} from './results';
import { DLFilterClick } from '@/js/general/data-layer';
import { RegionMenu } from './region-menu';

gsap.registerPlugin(ScrollTrigger);

const queryClient = new QueryClient();

export default function SearchAndFilter({
  isWebinar,
  shouldFetch,
}: Readonly<{
  isWebinar?: boolean;
  shouldFetch?: boolean;
}>) {
  const pageData = useRef<PageData>(getDataFromPage('react-search-data'));

  const {
    filters,
    type,
    enableAutomaticLoad,
    noResults: noResultsContent,
    noResultsFilters,
    searchTerms,
    apiUrl,
    regionFilterName,
    regions,
    strings,
    featuredContent,
    featuredContentPosition,
    hasUpcomingEvents,
  } = pageData.current;

  const typeHub = type === 'hub';
  const typeSearch = type === 'search';
  const typePeople = type === 'people';
  const typeEvents = type === 'events';

  const [filterData, setFilterData] = useState<Filter[]>(filters);
  const [appliedFilters, setAppliedFilters] = useState<Filter[]>(undefined);
  const [results, setResults] = useState<Result[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [openMenu, setOpenMenu] = useState<FilterName | false>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [featureRow, setFeatureRow] = useState(3);
  const [lastModified, setLastModified] = useState<'search' | 'filter' | null>(null);
  const [regionsState, setRegionsState] = useState(regions);

  const resultsRef = useRef<HTMLDivElement>(null);
  const hasInit = useRef(false);
  const noResultsString = useRef('');
  const noResultsFiltersString = useRef('');
  const combineResults = useRef(false);
  const renderResults = useRef(false);
  const submitSearch = useRef(false);
  const isScrollLoading = useRef(false);
  const hasRemovedContentArea = useRef(false);
  const isDesktop = useIsBreakpoint('lg');
  const isTablet = useIsBreakpoint('md');

  const openMobileMenu = () => setMobileMenuOpen(true);
  const closeMobileMenu = () => setMobileMenuOpen(false);
  const clearAllApplied = () => setAppliedFilters([]);

  const activeFilterGroup = openMenu
    ? filterData.find(filter => filter.name === openMenu)
    : undefined;

  useOutsideClick(
    '.filters__item-button, .filters__panel, .filters__panel-split',
    () => setOpenMenu(false)
  );

  const fetchData = async (): Promise<Data> => {
    const facetFilters = appliedFilters
      ? appliedFilters.map(filter => ({
          Name: filter.filterProp,
          Items: filter?.facets.map(facet => facet.id),
        }))
      : null;

    const res = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        PageSize: 10,
        Page: combineResults.current ? page : 1,
        SearchText: searchQuery,
        FacetFilters: facetFilters,
        ResultsOnly: combineResults.current,
        isWebinar,
      }),
    });

    return await res.json();
  };

  const { data, isFetching, isFetched, refetch } = useQuery({
    queryKey: ['search'],
    queryFn: fetchData,
    enabled: false,
  });

  const handleSelected: HandleSelectedArgs = facetId => {
    setFilterData(prev => {
      const updated = [...prev];
      for (const filter of updated) {
        const facet = filter.facets.find(f => f.id === facetId);

        if (facet) {
          facet.selected = !facet?.selected;
          break;
        }
      }
      return updated;
    });
  };

  const handleGroupSelectAll: HandleGroupSelectAllArgs = (name, selected) => {
    const updatedFilters = filterData.map(filter => {
      if (filter.name === name) {
        filter.facets = filter?.facets.map(facet => ({
          ...facet,
          selected,
        }));
      }

      return filter;
    });

    setFilterData(updatedFilters);
  };

  const handleMassSelect: HandleMassSelectArgs = (name, ids, selected) => {
    const updatedFilters = filterData.map(filter => {
      if (filter.name !== name) return filter;

      filter.facets = filter.facets.map(facet => {
        if (!ids.includes(facet.id)) return facet;

        return {
          ...facet,
          selected: selected ?? !facet.selected,
        };
      });

      return filter;
    });

    setFilterData(updatedFilters);
  };

  const handleFilterButton = (filterName: FilterName) => {
    const isSameMenu = filterName === openMenu;

    setOpenMenu(isSameMenu ? false : filterName);
  };

  const handleSearch = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    setMobileMenuOpen(false);
    appendSearchParamsToUrl(true);
    setLastModified('search');
    refetch();
    noResultsString.current = renderNoResults(noResultsContent);
  };

  const handlePillClick = (facetId: FacetId) => {
    setAppliedFilters(prev => {
      const updatedState = prev
        .map(filter => ({
          ...filter,
          facets: filter.facets.filter(facet => facet.id !== facetId),
        }))
        .filter(filter => filter.facets.length);

      return updatedState;
    });
  };

  const handleSearchTerm: HandleSearchTermArgs = (e, searchTerm) => {
    if (e.type === 'keydown' && (e as React.KeyboardEvent).key !== 'Enter')
      return;

    submitSearch.current = true;
    setSearchQuery(searchTerm);
  };

  const handleLoadMore = () => setPage(page + 1);

  const handleScrollLoad = throttle(() => {
    if (!resultsRef.current || isFetching) return;

    const { bottom } = resultsRef.current.getBoundingClientRect();
    const isBottomVisible = bottom < window.innerHeight - 100; // 100px offset

    if (isBottomVisible && shouldShowLoadMore) {
      isScrollLoading.current = true;
      handleLoadMore();
    }
  }, 250);

  const clearSelectedFiltersGroup = (filterName: FilterName) => {
    const updatedData = [...filterData];
    const category = updatedData.find(item => item.name === filterName);
    category.facets.forEach(facet => (facet.selected = false));

    setFilterData(updatedData);
  };

  const getAppliedFilters = () => {
    const selectedFacet = (facet: FilterFacet) => facet.selected;
    const selectedFilters = filterData
      .filter(item => item.facets.some(selectedFacet))
      .map(filter => ({
        ...filter,
        facets: filter.facets.filter(selectedFacet),
      }));

    return selectedFilters;
  };

  const getAppliedFiltersFromIds = (ids: FacetId[]) => {
    const updatedData = filterData
      .filter(filter => filter.facets.some(facet => ids.includes(facet.id)))
      .map(filter => ({
        ...filter,
        facets: filter.facets.filter(facet => ids.includes(facet.id)),
      }));

    return updatedData;
  };

  const applyFilters = () => {
    setAppliedFilters(getAppliedFilters());
    setOpenMenu(false);
    setLastModified('filter');
  };

  const getFlattenedFacets = () => {
    if (!appliedFilters) return [];
    return appliedFilters.map(filter => filter.facets).flat();
  };

  const appendSearchParamsToUrl = (withSearch: boolean = false) => {
    const selectedIds = getFlattenedFacets()
      .map(facet => facet.id)
      .join(',');

    const searchParams = new URLSearchParams();

    if (selectedIds.length)
      searchParams.append(URL_SEARCH_FILTERS, selectedIds);

    const addSearch = withSearch && searchQuery;

    if (addSearch) searchParams.append(URL_SEARCH_SEARCH, searchQuery);

    const query =
      selectedIds.length || addSearch ? `?${searchParams.toString()}` : '';

    window.history.pushState(null, '', query || window.location.pathname);
  };

  const applySearchParams = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const filters = searchParams.get(URL_SEARCH_FILTERS);
    const query = searchParams.get(URL_SEARCH_SEARCH);

    if (filters) {
      const filtersArray = filters.split(',');
      for (const item of filtersArray) {
        handleSelected(item);
      }

      setAppliedFilters(getAppliedFiltersFromIds(filtersArray));
    }

    if (query) {
      setSearchQuery(query);
    }
  };

  const renderNoResults = (content: string) => {
    const html = decode(decode(content));
    return html.replace(/%query%/i, encode(searchQuery));
  };

  const renderNoResultsFilters = (content: string) => {
    const html = decode(decode(content));
    return html;
  };

  const renderFilterButton = (filter: Filter) => {
    if (
      filter?.filterProp !== undefined &&
      filter.filterProp === regionFilterName &&
      (!regionsState || regionsState.length === 0)
    ) {
      return null;
    }

    return (
      <button
        key={`filter-button-${filter.name}`}
        className={clsx('filters__item-button', {
          open: filter.name === openMenu,
        })}
        onClick={() => handleFilterButton(filter.name)}
      >
        <span>{filter.name}</span>
        <div className="filters__item-button-icon"></div>
      </button>
    );
  };

  useEffect(() => {
    if (!isFetching || hasRemovedContentArea.current) return;

    const hubContent = document.querySelector<HTMLElement>(
      '.search__hub-content'
    );

    if (!hubContent) return;

    hubContent.style.display = 'none';
  }, [isFetching, hasRemovedContentArea]);

  useEffect(() => {
    if (!isFetched) return;

    renderResults.current = true;
  }, [isFetched]);

  useEffect(() => {
    if (!data) return;
    if (!combineResults.current) setFilterData(data.filters);
    if (data.regions) setRegionsState(data.regions);

    setResults(prev => {
      const previous = combineResults.current ? prev : [];
      combineResults.current = false;

      return [...previous, ...data.results];
    });

    if (!combineResults.current) setPage(1);
    isScrollLoading.current = false;

    if (!appliedFilters?.length) return;

    const category = appliedFilters?.map(item => item.name).join(', ');
    const clickText = getFlattenedFacets()
      .map(item => item.name)
      .join(', ');

    DLFilterClick({ category, click_text: clickText });
  }, [data]);

  useEffect(() => {
    if (hasInit.current === false && filterData.length) {
      applySearchParams();
      hasInit.current = true;
      if (noResultsFilters) {
        noResultsFiltersString.current = renderNoResultsFilters(noResultsFilters);
      }
    }
  }, [filterData, noResultsFilters]);

  useEffect(() => {
    if (hasInit.current) {
      appendSearchParamsToUrl(!!searchQuery);
    }

    if (appliedFilters) {
      refetch();
    }
  }, [appliedFilters]);

  useEffect(() => {
    if (!data || !appliedFilters) return;

    for (const filter of appliedFilters) {
      filter.facets.forEach(facet => handleSelected(facet.id));
    }
  }, [data, appliedFilters]);

  useEffect(() => {
    if (page <= 1) return;

    combineResults.current = true;
    refetch();
  }, [page]);

  useEffect(() => {
    if (searchQuery && submitSearch.current) {
      submitSearch.current = false;

      refetch();
    }
  }, [searchQuery, submitSearch]);

  useEffect(() => {
    if (isWebinar === undefined || !isFetched) return;
    refetch();
  }, [isWebinar]);

  useEffect(() => {
    if (!shouldFetch) return;
    refetch();
  }, [shouldFetch]);

  useEffect(() => {
    if (!enableAutomaticLoad) return;
    if (isDesktop)
      return window.removeEventListener('scroll', handleScrollLoad);

    window.addEventListener('scroll', handleScrollLoad);

    return () => window.removeEventListener('scroll', handleScrollLoad);
  }, [handleScrollLoad, isDesktop]);

  useEffect(() => {
    ScrollTrigger.refresh(true);
  }, [results]);

  useEffect(() => {
    // Calculate what row the featured content should be placed given that:
    // 1. The feature card spans an entire row.
    // 2. The results have three different column densities across breakpoints (1, 2 and 3).
    // 3. The featured content does not show after a incomplete row.
    // 4. 'featuredContentPosition' is the number of result items that should display before the featured content.

    let alpha = 1;
    if (isTablet) alpha = 2;
    if (isDesktop) alpha = 3;

    const fullRows = Math.floor(results.length / alpha);
    const safePosition = alpha * Math.ceil(featuredContentPosition / alpha);
    const rowPosition = safePosition / alpha + 1;

    if (results.length === 0) return setFeatureRow(1);
    if (rowPosition > fullRows) return setFeatureRow(fullRows + 1);
    setFeatureRow(rowPosition);
  }, [results, isTablet, isDesktop]);

  // Rendering conditions
  const shouldShowFilters =
    filterData.some(filter => filter.facets.length > 0) || typeHub;
  const shouldShowPills = appliedFilters?.length;
  const shouldShowResults = renderResults.current && !!results.length;
  const shouldShowLoadMore = page < data?.totalPages && renderResults.current;
  const shouldShowSkeleton =
    isFetching && (!isScrollLoading.current || isDesktop);
  const shouldShowPopular =
    results.length === 0 && searchTerms?.length && !isFetching;
  const shouldShowNoResults =
    results.length === 0 && renderResults.current && (!!noResultsContent || !!noResultsFilters);

  const filterMenuButtons = openMenu ? (
    <div className="filters__buttons">
      <Button
        className="button-primary"
        onClick={applyFilters}
        content={strings.applyFilters}
      />
      <button
        type="button"
        className="button-tertiary"
        disabled={!activeFilterGroup.facets.some(facet => facet.selected)}
        onClick={() => clearSelectedFiltersGroup(activeFilterGroup.name)}
      >
        {strings.clearFilters}
      </button>
    </div>
  ) : null;

  return (
    <div className="react-search__inner">
      {typeSearch ? (
        <Search
          handleSearch={handleSearch}
          searchPlaceHolder={strings.searchPlaceHolder}
          searchCta={strings.searchCta}
          onChange={e => setSearchQuery(e.target.value)}
          searchQuery={searchQuery}
        />
      ) : null}

      {!typeEvents ||
      (typeEvents && hasUpcomingEvents) ||
      (typeEvents && isWebinar) ? (
        <div className="filters">
          <div
            className={clsx('filters__inner', {
              'knowledge-hub': typeHub || typePeople || typeEvents,
            })}
          >
            {shouldShowFilters ? (
              <div className="filters__filters">
                <div className="filters__title">{strings.exploreBy}</div>

                {filterData.map(renderFilterButton)}
              </div>
            ) : null}

            {typeHub || typePeople || typeEvents ? (
              <InlineSearch
                handleSearch={handleSearch}
                searchPlaceHolder={strings.searchPlaceHolder}
                searchCta={strings.searchCta}
                onChange={e => setSearchQuery(e.target.value)}
                searchQuery={searchQuery}
              />
            ) : null}
          </div>

          {shouldShowFilters ? (
            <>
              <hr className="filters__rule rule rule--full-width" />
              <Button
                className="filters__menu-button"
                onClick={openMobileMenu}
                childLeft={<i className="icon-filter"></i>}
                content={strings.filterAndSeach}
              />
            </>
          ) : null}

          {filterData ? (
            <FilterMenu
              isOpen={mobileMenuOpen}
              filters={filterData}
              showSearch={typeHub || typePeople || typeEvents}
              onClose={closeMobileMenu}
              applyFilters={() => {
                applyFilters();
                closeMobileMenu();
              }}
              handleReset={clearAllApplied}
              handleSelected={handleSelected}
              handleGroupSelectAll={handleGroupSelectAll}
              handleMassSelect={handleMassSelect}
              handleSearch={handleSearch}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              strings={strings}
              regionFilterName={regionFilterName}
              regions={regions}
            />
          ) : null}

          {!!openMenu &&
            (regionFilterName === activeFilterGroup.filterProp ? (
              <RegionMenu
                regions={regionsState}
                locations={activeFilterGroup}
                handleSelected={handleSelected}
                handleMassSelect={handleMassSelect}
                buttonSection={filterMenuButtons}
              />
            ) : (
              <div className="filters__panel">
                <ul className="filters__panel-list">
                  {activeFilterGroup?.facets.length >= 2 ? (
                    <li>
                      <Checkbox.SelectAll
                        filter={activeFilterGroup}
                        callback={() =>
                          handleGroupSelectAll(
                            activeFilterGroup.name,
                            !activeFilterGroup.facets.every(
                              facet => facet.selected
                            )
                          )
                        }
                      />
                    </li>
                  ) : null}
                  {activeFilterGroup?.facets.map(facet => (
                    <li key={`checkbox-item-${facet.id}`}>
                      <Checkbox
                        facet={facet}
                        callback={() => handleSelected(facet.id)}
                      />
                    </li>
                  ))}
                </ul>
                {filterMenuButtons}
              </div>
            ))}
        </div>
      ) : null}

      {!typeEvents ||
      (typeEvents && hasUpcomingEvents) ||
      (typeEvents && isWebinar) ? (
        <div className="filters__info">
          {shouldShowPills ? (
            <div className="filters__pills">
              <button
                className="filters__pill filters__pill--dark"
                type="button"
                onClick={clearAllApplied}
              >
                {strings.clearAll}
              </button>
              {getFlattenedFacets().map(facet => (
                <button
                  className="filters__pill"
                  type="button"
                  key={`pill-${facet.id}`}
                  onClick={() => handlePillClick(facet.id)}
                >
                  {facet.name}
                </button>
              ))}
            </div>
          ) : null}

          {data ? (
            <p className="filters__indexes">{data.showingResultsText}</p>
          ) : null}
        </div>
      ) : null}

      {shouldShowResults ? (
        <div
          ref={resultsRef}
          className={clsx('search-results', {
            'knowledge-hub': typeHub,
            people: typePeople,
            events: typeEvents,
          })}
          style={{ '--feature-row': featureRow } as CSSProperties}
        >
          <Suspense fallback={<Skeleton />}>
            <>
              {results.map(result => {
                let Result = SearchResult;
                if (typeHub) Result = CardResult;
                if (typePeople) Result = PersonResult;
                if (typeEvents) Result = EventResult;

                return (
                  <Result key={`search-result-${result.id}`} {...result} />
                );
              })}
              {featuredContent ? (
                <CardResult isFeatured {...featuredContent[0]} />
              ) : null}
              {isScrollLoading.current && !isDesktop ? <Skeleton /> : null}
            </>
          </Suspense>
        </div>
      ) : null}

      {shouldShowSkeleton ? (
        <div
          className={clsx('search-results', {
            'knowledge-hub': typeHub,
            people: typePeople,
            events: typeEvents,
          })}
        >
          <Skeleton amount={typePeople ? 4 : 6} />
        </div>
      ) : null}

      {shouldShowLoadMore ? (
        <div className="search-results__load">
          <Button
            className="search-results__load-button"
            disabled={isFetching}
            content={strings.loadMore}
            childRight={<i className="icon-arrow-down" />}
            onClick={handleLoadMore}
          />
        </div>
      ) : null}

      {shouldShowNoResults ? (
        <div>
          {lastModified === 'search' && searchQuery && noResultsString.current ? (
            <div
              className="search-results__no-results"
              dangerouslySetInnerHTML={{
                __html: noResultsString.current,
              }}
            />
          ) : null}
          {lastModified === 'filter' && appliedFilters?.length && noResultsFiltersString.current ? (
            <div
              className="search-results__no-results"
              dangerouslySetInnerHTML={{
                __html: noResultsFiltersString.current,
              }}
            />
          ) : null}
          {featuredContent ? (
            <CardResult isFeatured {...featuredContent[0]} />
          ) : null}
        </div>
      ) : null}

      {shouldShowPopular ? (
        <PopularSearches
          searchTerms={searchTerms}
          title={strings.popularSearches}
          onClick={handleSearchTerm}
        />
      ) : null}
    </div>
  );
}

rootRender(
  'react-search',
  <QueryClientProvider client={queryClient}>
    <SearchAndFilter />
  </QueryClientProvider>
);
